import {createSelector} from 'reselect'
import {OCCUPANCY_STATUS_CURRENT, OCCUPANCY_STATUS_NOTICE, OCCUPANCY_STATUS_FUTURE} from '../constants/ModelConstants'

/**
 * @returns {Array<RichResidentObject>}
 */
export const selectRichResidentObjectsFromAppState = createSelector(
  [state => state.Residents.residentsLookup, state => state.Occupancies.occupanciesLookup, state => state.Units.unitsLookup],
  (rLookup, oLookup, uLookup) => {
    return Object.values(rLookup).map(r => getRichResidentObject(rLookup, oLookup, uLookup, r.id))
  },
)

/**
 * @param {string} status
 * @return {boolean}
 */
export function isStatusActive(status) {
  // This should use the same logic as in /dep/backend-helpers/YardiAPIHelper.js -> isStatusActive
  return [OCCUPANCY_STATUS_CURRENT, OCCUPANCY_STATUS_NOTICE, OCCUPANCY_STATUS_FUTURE].includes(status)
}

/**
 * @param {RichResidentObject} residentObj
 * @return {boolean}
 */
export function isResidentActive(residentObj) {
  if (!residentObj) {
    return false
  }
  return isStatusActive(residentObj.statusOverride || residentObj.occupancy.status) || residentObj.isTest // Dummy residents should always be included, regardless of their status, because they need to remain editable from the dummy account page.
}

/**
 * @returns {Array<RichResidentObject>}
 */
export const getActiveResidents = createSelector([selectRichResidentObjectsFromAppState], richResidentsArray =>
  richResidentsArray.filter(r => isResidentActive(r)),
)

/**
 * @returns {function():Array<RichResidentObject>}
 */
export const makeSelectResidentsByUnit = (unitId, includePast = false) =>
  createSelector([selectRichResidentObjectsFromAppState], residents => {
    if (includePast) {
      return residents.filter(r => r.occupancy.unit.id === unitId)
    }
    return residents.filter(r => r.occupancy.unit.id === unitId && isResidentActive(r))
  })

/**
 * @returns {function():Array<RichResidentObject>}
 */
export const makeSelectResidentsByOccupancy = (occupancyId, includePast = false) =>
  createSelector([selectRichResidentObjectsFromAppState], residents => {
    if (includePast) {
      return residents.filter(r => r.occupancy.id === occupancyId)
    }
    return residents.filter(r => r.occupancy.id === occupancyId && isResidentActive(r))
  })

// ----------------------------------------------------------------------------------------------------
// these types and functions are related to RichResidentObject creation

/**
 * TODO: There are actually other possible props included in here based off the Resident, Occupancy, and Unit Fragments, would like to document
 * @typedef {Object} RichResidentObject
 * @property {string} id
 * @property {string} displayName
 * @property {{
 *    id: string,
 *    leaseToDate?: string
 *    leaseFromDate?: string
 *    status?: string,
 *    unit: {
 *      id: string,
 *      unitBuildingId: string,
 *      number: string
 *      deliveryPTE: boolean,
 *    }
 * }} occupancy
 * @property {*} userProfileObj
 * // optional props
 * @property {string?} canonnicalDisplayName
 * @property {string?} moveInDate
 * @property {string?} moveOutDate
 * @property {string?} phoneNumber
 * @property {string?} email
 * @property {string?} referenceId
 * @property {string?} secondaryReferenceId
 * @property {string?} statusOverride
 * @property {boolean?} _notFound -- true IFF this safe object have placeholder data
 */

/** @type RichResidentObject */
const EMPTY_OBJECT = {
  id: '',
  displayName: '',
  occupancy: {
    id: '',
    unit: {
      id: '',
      unitBuildingId: '',
      number: '',
      deliveryPTE: false,
    },
  },
  userProfileObj: {},
  _notFound: true, // the empty object has the notFound flag to true indicating it's not a real resident object
}

/**
 * @param {Object<string, ResidentFragment>} residentsLookup
 * @param {Object<string, OccupancyFragment>} occupanciesLookup
 * @param {Object<string, UnitFragment>} unitsLookup
 * @param {string} residentId
 * @return {RichResidentObject}
 */
export function getRichResidentObject(residentsLookup, occupanciesLookup, unitsLookup, residentId) {
  const resident = residentsLookup[residentId]

  if (!resident) {
    // return an empty object if not found
    return {...EMPTY_OBJECT, id: residentId}
  }

  let occupancy = occupanciesLookup[resident.residentOccupancyId]
  if (!occupancy) {
    occupancy = {...EMPTY_OBJECT.occupancy}
  }

  let unit = unitsLookup[occupancy.occupancyUnitId]
  if (!unit) {
    unit = {...EMPTY_OBJECT.occupancy.unit}
  }

  /** @type {RichResidentObject} */
  return {
    ...resident,
    occupancy: {
      ...occupancy,
      unit: unit,
    },
  }
}
